import React from "react";
import { Link } from "react-router-dom";
import { Footer } from "./App";

export default function About() {
  return (
    <main>
      <section className="about-hero">
        <span className="mono-label">About — established 2023</span>
        <h1>We don&rsquo;t take sides. We take <em>sources.</em></h1>
        <p className="lead">
          FactOrNot. is an independent, automated fact-checker focused on one job: turning a fuzzy claim into a clear, sourced verdict — without the hot takes, the hedging, or the algorithmic outrage.
        </p>
      </section>

      <section className="about-grid">
        <aside className="about-sticky">
          <span className="mono-label">Section 01</span>
          <h3>Our mission</h3>
        </aside>
        <div className="about-body">
          <p>
            We started FactOrNot after watching the same misleading screenshots circle through three group chats in a single morning. The information was wrong. The corrections — buried two pages deep on a newspaper website — were right but invisible. <strong>Our goal is to flip that ratio.</strong>
          </p>
          <p>
            We exist to make the verdict on a claim as easy to find, share, and screenshot as the original misinformation. That means short answers, primary sources, and a colour you can read across the room: <em>green, amber, or red</em>.
          </p>
          <div className="pullquote">If a claim is worth sharing, it&rsquo;s worth a 30-second check.</div>
          <p>
            We are <strong>not</strong> a political project. We do not endorse candidates, parties, or platforms. Roughly half of the claims people run through us come from someone asking about something a friend or family member shared — not from headlines.
          </p>
        </div>

        <aside className="about-sticky">
          <span className="mono-label">Section 02</span>
          <h3>Principles</h3>
        </aside>
        <div className="about-body">
          <p>
            Every verdict we produce has to clear the same four bars before it goes live. If a check can&rsquo;t clear them, we surface the uncertainty instead of faking an answer.
          </p>
          <div className="principles">
            <div className="principle">
              <div className="icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12 9 18 21 6" />
                </svg>
              </div>
              <h4>Primary sources first</h4>
              <p>We cite the document, dataset, or paper itself — not a tweet about it, not a headline about it.</p>
            </div>
            <div className="principle">
              <div className="icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v6l4 2" />
                </svg>
              </div>
              <h4>Show our work</h4>
              <p>Every verdict shows the primary sources used and the reasoning behind the call. Audit any check end-to-end.</p>
            </div>
            <div className="principle">
              <div className="icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2 3 7v6c0 5 4 8 9 9 5-1 9-4 9-9V7Z" />
                </svg>
              </div>
              <h4>Independence</h4>
              <p>No campaign money. No platform money. Just a tool that does the work — automated, repeatable, and out in the open.</p>
            </div>
            <div className="principle">
              <div className="icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-3-6.7" />
                  <path d="M21 4v5h-5" />
                </svg>
              </div>
              <h4>Correction-first</h4>
              <p>If we get something wrong, we change the verdict, mark it, and note exactly what we missed.</p>
            </div>
          </div>
        </div>

        <aside className="about-sticky">
          <span className="mono-label">Section 03</span>
          <h3>How we got here</h3>
        </aside>
        <div className="about-body">
          <div className="timeline">
            <div className="tl-row">
              <div className="tl-year">2025</div>
              <div className="tl-body">
                <h4>The project starts</h4>
                <p>Born out of frustration with misleading screenshots flying around group chats. We build a small prototype that takes a claim, searches the web, and returns a sourced verdict.</p>
              </div>
            </div>
            <div className="tl-row">
              <div className="tl-year">2025</div>
              <div className="tl-body">
                <h4>Automated pipeline goes live</h4>
                <p>Verdicts drop from days to seconds. Every check pulls primary sources, weighs them, and returns an answer you can audit.</p>
              </div>
            </div>
            <div className="tl-row">
              <div className="tl-year">2026</div>
              <div className="tl-body">
                <h4>A redesign — and you&rsquo;re looking at it</h4>
                <p>Faster checks, plain-language verdicts, and an archive you can actually browse on your phone.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta" style={{ marginTop: 88 }}>
        <div className="cta-inner">
          <h2>Got a claim that&rsquo;s been bugging you? <em>Send it over.</em></h2>
          <div className="cta-actions">
            <Link to="/" className="btn btn-line">Try a check</Link>
            <Link to="/login" className="btn btn-green">Create an account →</Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
